import {createSelector} from "@reduxjs/toolkit";

import {resolveActiveParameterValue, toAvmPayloadValue} from "@reva/shared/parameter-resolution";
import type {RevaIndicationEndpoint, RevaSpecInputParameter} from "@reva/shared/spec-conversion";

import type {ValueIndicationRequest} from "../api/payload-types/value-indication";
import {currySelector, selectArg} from "../util/selectors";
import {getValueAndLendingValue} from "../util/valueIndicationUtil";
import {selectAddressParam} from "./addressSlice";
import {
    selectAllEnergyParams,
    selectAllFirstFinalizeParams,
    selectAllIdpEnergyCertParams,
    selectAllImmodropParams,
    selectAllInitialValuationParams,
    selectAllInspectionDerivedParams,
    selectAllInspectionParams,
    selectAllParams,
    selectAllPlotParams,
    selectAllRegisterParams,
    selectHasSpfTable,
    selectSegment,
} from "./objectParametersSlice";
import {selectSpecApiParamInputs, selectSpecApiParamOutputs} from "./specSlice";
import type {RevaRootState} from "./store";
import {selectCombinedIndicationData} from "./valueIndicationSlice";

/** Spec input map of an indication endpoint (segment → parameter-name → input parameter definition). */
type IndicationInputSpec = Record<string, Record<string, RevaSpecInputParameter>>;

/**
 * Select the payload JSON to be sent to the value indication endpoint.
 */
export const selectCurrentIndicationPayload = (state: RevaRootState): ValueIndicationRequest | null => {
    const indicationSpec = selectSpecApiParamInputs(state);
    return selectCurrentIndicationPayloadForSpec(state, indicationSpec);
};

/**
 * Select the payload JSON to be sent to the value indication endpoint with the given inputSpec.
 */
export const selectCurrentIndicationPayloadForSpec = createSelector(
    [
        selectArg<IndicationInputSpec | null>,
        selectAddressParam,
        selectSegment,
        selectAllParams,
        selectAllInitialValuationParams,
        selectAllInspectionParams,
        selectAllInspectionDerivedParams,
        selectAllPlotParams,
        selectAllEnergyParams,
        selectAllIdpEnergyCertParams,
        selectAllRegisterParams,
        selectAllFirstFinalizeParams,
        selectAllImmodropParams,
        selectHasSpfTable,
    ],
    (
        inputSpec,
        address,
        segment,
        params,
        initialValuationsParams,
        inspectionParams,
        inspectionDerivedParams,
        plotParams,
        energyParams,
        idpEnergyCertParams,
        registerParams,
        firstFinalizeParams,
        immodropParams,
        hasSpfTable,
    ): ValueIndicationRequest | null => {
        if (!address || !segment || !params || !inputSpec?.[segment]) return null;

        // TODO: ObjectId?, Accounting?
        const payload: ValueIndicationRequest = {address, segment};
        for (const [paramName] of Object.entries(inputSpec[segment])) {
            const reva = (params as Record<string, unknown>)[paramName];
            const initialValuation = (initialValuationsParams as Record<string, unknown>)[paramName];
            const inspection = (inspectionParams as Record<string, unknown>)[paramName];
            const energy = (energyParams as Record<string, unknown>)[paramName];
            const idpEnergyCertificate = (idpEnergyCertParams as Record<string, unknown>)[paramName];
            const register = (registerParams as Record<string, unknown>)[paramName];
            const inspectionDerived = (inspectionDerivedParams as Record<string, unknown>)[paramName];
            const plot = (plotParams as Record<string, unknown>)[paramName];
            const firstFinalize = (firstFinalizeParams as Record<string, unknown>)[paramName];
            const immodrop = (immodropParams as Record<string, unknown>)[paramName];
            const parameters = {
                reva,
                initialValuation,
                inspection,
                inspectionDerived,
                plot,
                energy,
                idpEnergyCertificate,
                register,
                firstFinalize,
                immodrop,
                parameter: paramName,
                hasSpfTable,
            };
            const value = toAvmPayloadValue(
                paramName,
                resolveActiveParameterValue("AVM", parameters).activeParameterValue,
            );
            if (value !== undefined) payload[paramName] = value;
        }

        return payload;
    },
);

/** Segment, building type and rented flag of the current indication. */
export interface IndicationSegmentBuildingTypeRentedData {
    segment: string;
    building_type?: number | null;
    prop_rented?: boolean | null;
}

/**
 * Select the segment, building type and rented property of the current indication.
 */
export const selectSegmentBuildingTypeRentedIndicationPayload = createSelector(
    [selectCurrentIndicationPayload],
    (payload): IndicationSegmentBuildingTypeRentedData | null => {
        if (!payload?.segment) return null;

        return {
            segment: payload.segment,
            building_type: payload.building_type,
            prop_rented: payload.prop_rented,
        };
    },
);

const selectValueAndLendingValue = createSelector(
    [
        selectCombinedIndicationData,
        selectSpecApiParamOutputs,
        selectSegment,
        selectArg<RevaIndicationEndpoint | null | undefined>,
    ],
    (combinedIndicationData, outputParams, segment, endpoint) =>
        getValueAndLendingValue(combinedIndicationData, outputParams, segment, endpoint),
);

/**
 * Curried selector to get the value and lending value from the combined indication data for a
 * given endpoint.
 */
export const selectValueAndLendingValueCurried = currySelector(selectValueAndLendingValue);
